import React, { useState, useEffect } from 'react';
import { Download, Calendar, User, FileSpreadsheet } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';

const AttendanceDownloadPDF = ({ isAdmin = false, employeeData = null }) => {
  const [reportScope, setReportScope] = useState('single'); // 'single' | 'all'
  const [selectedEmployees, setSelectedEmployees] = useState([]); // array of employee IDs for multi-select
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [dateRangeMode, setDateRangeMode] = useState('monthly'); // 'monthly' or 'range'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Generate years (current year and 2 years back)
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  useEffect(() => {
    // Set default to current month and year
    const now = new Date();
    setSelectedMonth(String(now.getMonth() + 1));
    setSelectedYear(String(now.getFullYear()));

    // Fetch employees if admin
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await api.get('/admin/employees');
      // Filter out inactive employees - only show active employees in dropdown
      const activeEmployees = normalizeUserProfiles(response.data.employees || []).filter(emp => emp.isActive !== false);
      setEmployees(activeEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to fetch employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchAttendanceData = async (employeeId, month, year) => {
    try {
      const endpoint = isAdmin 
        ? `/admin/attendance/employee/${employeeId}?month=${month}&year=${year}`
        : `/employee/attendance/history?month=${month}&year=${year}`;
      
      const response = await api.get(endpoint);
      return response.data.attendances || [];
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      throw error;
    }
  };

  const fetchEmployeeDetails = async (employeeId) => {
    try {
      const response = await api.get(`/admin/employees`);
      const employee = normalizeUserProfiles(response.data.employees || []).find(emp => getUserIdentifier(emp) === employeeId);
      return employee;
    } catch (error) {
      console.error('Error fetching employee details:', error);
      throw error;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatTotalHours = (hours) => {
    if (!hours || hours === 0) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusText = (status) => {
    const statusMap = {
      'full-day': 'Full Day',
      'half-day': 'Half Day',
      'absent': 'Absent'
    };
    return statusMap[status] || status;
  };

  const getDaysInMonth = (year, month) => {
    const daysArray = [];
    const daysInMonth = new Date(year, month, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      daysArray.push({
        date: day,
        dayName: dayNames[date.getDay()],
        fullDate: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }
    
    return daysArray;
  };

  const getDaysInRange = (startDateStr, endDateStr) => {
    const daysArray = [];
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    
    const current = new Date(start);
    while (current <= end) {
      const year = current.getFullYear();
      const month = current.getMonth() + 1;
      const day = current.getDate();
      
      daysArray.push({
        date: day,
        dayName: dayNames[current.getDay()],
        fullDate: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        displayDate: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return daysArray;
  };

  const isFutureDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date > today;
  };

  // Recurring rule: the 2nd and 4th Saturday of every month are system week-offs for
  // HO (and anyone with no branch assigned). SSbranch employees work every Saturday -
  // no alternate week-off - matching hrms-backend/utils/attendanceStatus.js and
  // EmployeeAttendanceCalendar.js so every attendance view agrees on which Saturdays
  // are holidays. Takes the day-of-month and day-name already computed for the row
  // (not a re-parsed date string) so this never drifts from the local-time weekday
  // getDaysInMonth/getDaysInRange already assigned to that row.
  const isAltSaturdayHoliday = (dayOfMonth, dayName, branch) => {
    if (branch === 'SSbranch') return false;
    if (dayName !== 'Saturday') return false;
    return Math.ceil(dayOfMonth / 7) % 2 === 0;
  };

  // Build table data and stats for one employee (for reuse in single + all-employee reports)
  const buildEmployeeReportData = async (employee, month, year, holidayMap) => {
    const employeeId = getUserIdentifier(employee);
    const attendanceData = await fetchAttendanceData(employeeId, month, year);

    const leavesResponse = await api.get(
      `/admin/employee/${employeeId}/approved-leaves?month=${month}&year=${year}`
    );
    const approvedLeaves = leavesResponse.data.leaves || [];

    const wfhResponse = await api.get(
      `/admin/employee/${employeeId}/approved-wfh?month=${month}&year=${year}`
    );
    const approvedWfh = wfhResponse.data.wfhRequests || [];

    const leaveMap = {};
    approvedLeaves.forEach(leave => {
      leave.leaveDays?.forEach(day => {
        const dayDate = new Date(day.date);
        const dateKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
        leaveMap[dateKey] = day;
      });
    });

    const wfhMap = {};
    approvedWfh.forEach(wfh => {
      wfh.wfhDays?.forEach(day => {
        const dayDate = new Date(day.date);
        const dateKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
        wfhMap[dateKey] = day;
      });
    });

    const attendanceMap = {};
    attendanceData.forEach(att => {
      const attDate = new Date(att.date);
      const dateKey = `${attDate.getFullYear()}-${String(attDate.getMonth() + 1).padStart(2, '0')}-${String(attDate.getDate()).padStart(2, '0')}`;
      attendanceMap[dateKey] = att;
    });

    const reportTitle = `${monthNames[parseInt(month) - 1]} ${year}`;
    const allDays = getDaysInMonth(parseInt(year), parseInt(month)).map(day => ({
      ...day,
      displayDate: `${String(day.date).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`
    }));

    const tableData = allDays.map(day => {
      const attendance = attendanceMap[day.fullDate];
      const isFuture = isFutureDate(day.fullDate);
      const isHoliday = holidayMap[day.fullDate];
      const isSunday = day.dayName === 'Sunday';
      const isAltSaturday = isAltSaturdayHoliday(day.date, day.dayName, employee?.branch);
      const leaveDay = leaveMap[day.fullDate];
      const wfhDay = wfhMap[day.fullDate];

      if (isFuture) {
        return [day.displayDate, day.dayName, '-', '-', '-', 'Future'];
      }
      if (isHoliday) {
        return [day.displayDate, `${day.dayName} (${isHoliday})`, '-', '-', '-', 'Full Day'];
      }
      if (isSunday || isAltSaturday) {
        return [day.displayDate, day.dayName, '-', '-', '-', 'Full Day'];
      }
      if (leaveDay) {
        const leaveTypeText = leaveDay.leaveType === 'earned' ? 'Earned' : leaveDay.leaveType === 'sick' ? 'Sick' : leaveDay.leaveType === 'comp_off' ? 'Comp Off' : leaveDay.leaveType === 'casual' ? 'Casual' : 'W/O Pay';
        const dayTypeText = leaveDay.dayType === 'half' ? 'Half Day' : 'Full Day';
        return [day.displayDate, day.dayName, '-', '-', '-', `${dayTypeText} ${leaveTypeText} Leave`];
      }
      if (wfhDay) {
        const dayTypeText = wfhDay.dayType === 'half' ? 'Half Day' : 'Full Day';
        return [day.displayDate, day.dayName, '-', '-', '-', `${dayTypeText} WFH (Present)`];
      }
      if (!attendance) {
        return [day.displayDate, day.dayName, '-', '-', '-', 'Absent'];
      }
      return [
        day.displayDate,
        day.dayName,
        formatTime(attendance.checkInTime),
        formatTime(attendance.checkOutTime),
        formatTotalHours(attendance.totalHours),
        getStatusText(attendance.status)
      ];
    });

        // fullDay/halfDay/absent are tallied straight off tableData's Status column (not
        // recomputed separately from attendanceData), so the summary always reconciles
        // with the day-by-day rows built above for this employee's calendar month.
        const stats = {
          fullDay: tableData.filter(row => row[5] === 'Full Day').length,
      halfDay: tableData.filter(row => row[5] === 'Half Day').length,
      absent: tableData.filter(row => row[5] === 'Absent').length,
      totalHours: attendanceData.reduce((sum, att) => sum + (att.totalHours || 0), 0),
      earnedLeaves: approvedLeaves.reduce((sum, leave) =>
        sum + leave.leaveDays.filter(day => day.leaveType === 'earned').reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0),
      sickLeaves: approvedLeaves.reduce((sum, leave) =>
        sum + leave.leaveDays.filter(day => day.leaveType === 'sick').reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0),
      casualLeaves: approvedLeaves.reduce((sum, leave) =>
        sum + leave.leaveDays.filter(day => day.leaveType === 'casual').reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0),
      withoutPayLeaves: approvedLeaves.reduce((sum, leave) =>
        sum + leave.leaveDays.filter(day => day.leaveType === 'without_pay').reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0),
      compOffLeaves: approvedLeaves.reduce((sum, leave) =>
        sum + leave.leaveDays.filter(day => day.leaveType === 'comp_off').reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0)
    };

    return { tableData, stats, allDays, reportTitle };
  };

  const generatePDF = async () => {
    const isAllEmployees = isAdmin && reportScope === 'all';
    const isMultiSelect = isAdmin && reportScope === 'single' && selectedEmployees.length > 1;
    if (isAdmin && reportScope === 'single' && selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    if (isAllEmployees && dateRangeMode !== 'monthly') {
      toast.error('All Employee report is available for monthly report only. Please select Month and Year.');
      return;
    }

    // Validate based on mode
    if (dateRangeMode === 'monthly') {
      if (!selectedMonth || !selectedYear) {
        toast.error('Please select month and year');
        return;
      }
    } else {
      if (!startDate || !endDate) {
        toast.error('Please select start and end dates');
        return;
      }
      if (new Date(startDate) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    try {
      setLoading(true);

      console.log('Starting PDF generation...');
      console.log('isAdmin:', isAdmin);
      console.log('employeeData:', employeeData);
      console.log('dateRangeMode:', dateRangeMode);

      // Determine month/year from mode
      let month, year;
      if (dateRangeMode === 'monthly') {
        month = selectedMonth;
        year = selectedYear;
      } else {
        // Use the start date's month/year for range mode
        const start = new Date(startDate);
        month = String(start.getMonth() + 1);
        year = String(start.getFullYear());
      }

      console.log('selectedMonth:', month);
      console.log('selectedYear:', year);

      // --- Multi-select employees PDF: multiple pages for selected employees only ---
      if (isMultiSelect) {
        const holidaysResponse = await api.get('/admin/holidays');
        const allHolidays = holidaysResponse.data || [];
        const holidayMap = {};
        allHolidays.forEach(holiday => {
          const d = new Date(holiday.date);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          holidayMap[key] = holiday.description;
        });

        const employeesToReport = employees.filter(emp => selectedEmployees.includes(emp._id));
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.width;
        const pageHeight = pdf.internal.pageSize.height;
        let pageNum = 0;

        for (let i = 0; i < employeesToReport.length; i++) {
          const employee = employeesToReport[i];
          const { tableData, stats, reportTitle } = await buildEmployeeReportData(employee, month, year, holidayMap);

          if (pageNum > 0) pdf.addPage();
          pageNum++;

          pdf.setFillColor(139, 92, 246);
          pdf.rect(0, 0, pageWidth, 40, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(20);
          pdf.setFont(undefined, 'bold');
          pdf.text('ATTENDANCE REPORT', pageWidth / 2, 15, { align: 'center' });
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'normal');
          pdf.text(reportTitle, pageWidth / 2, 25, { align: 'center' });

          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'bold');
          pdf.text('Employee Name:', 14, 50);
          pdf.setFont(undefined, 'normal');
          pdf.text(employee.name || 'N/A', 55, 50);
          pdf.setFontSize(9);
          pdf.setTextColor(100, 100, 100);
          pdf.text(`Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth - 14, 50, { align: 'right' });

          autoTable(pdf, {
            startY: 60,
            head: [['Date', 'Day', 'Check-In', 'Check-Out', 'Total Hours', 'Status']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10, halign: 'center' },
            bodyStyles: { fontSize: 9, textColor: [0, 0, 0] },
            columnStyles: {
              0: { halign: 'center', cellWidth: 28 },
              1: { halign: 'left', cellWidth: 28 },
              2: { halign: 'center', cellWidth: 22 },
              3: { halign: 'center', cellWidth: 22 },
              4: { halign: 'center', cellWidth: 26 },
              5: { halign: 'center', cellWidth: 34 }
            },
            alternateRowStyles: { fillColor: [245, 245, 250] },
            margin: { top: 60, left: 14, right: 14 }
          });

          const finalY = pdf.lastAutoTable.finalY + 10;
          const summaryHeight = 70;
          if (finalY + summaryHeight > pageHeight - 20) {
            pdf.addPage();
            pageNum++;
          }
          const summaryY = finalY + summaryHeight > pageHeight - 20 ? 20 : finalY;
          pdf.setFillColor(249, 250, 251);
          pdf.rect(14, summaryY, pageWidth - 28, 60, 'F');
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text('ATTENDANCE SUMMARY', 20, summaryY + 8);
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'normal');
          const colWidth = (pageWidth - 28) / 4;
          const sy = summaryY + 16;
          pdf.setFont(undefined, 'bold');
          pdf.text('Full Days:', 20, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(34, 197, 94);
          pdf.text(String(stats.fullDay), 20, sy + 6);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'bold');
          pdf.text('Half Days:', 20 + colWidth, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(234, 179, 8);
          pdf.text(String(stats.halfDay), 20 + colWidth, sy + 6);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'bold');
          pdf.text('Absent:', 20 + colWidth * 2, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(239, 68, 68);
          pdf.text(String(stats.absent), 20 + colWidth * 2, sy + 6);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'bold');
          pdf.text('Total Hours:', 20 + colWidth * 3, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(139, 92, 246);
          pdf.text(formatTotalHours(stats.totalHours), 20 + colWidth * 3, sy + 6);
        }

        const fileName = `Attendance_${employeesToReport.length}_Employees_${monthNames[parseInt(month) - 1]}_${year}.pdf`;
        pdf.save(fileName);
        toast.success('PDF downloaded successfully!');
        setLoading(false);
        return;
      }

      // --- All Employees PDF: multiple pages, one per employee ---
      if (isAllEmployees) {
        const holidaysResponse = await api.get('/admin/holidays');
        const allHolidays = holidaysResponse.data || [];
        const holidayMap = {};
        allHolidays.forEach(holiday => {
          const d = new Date(holiday.date);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          holidayMap[key] = holiday.description;
        });

        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.width;
        const pageHeight = pdf.internal.pageSize.height;
        let pageNum = 0;

        for (let i = 0; i < employees.length; i++) {
          const employee = employees[i];
          const { tableData, stats, reportTitle } = await buildEmployeeReportData(employee, month, year, holidayMap);

          if (pageNum > 0) pdf.addPage();
          pageNum++;

          pdf.setFillColor(139, 92, 246);
          pdf.rect(0, 0, pageWidth, 40, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(20);
          pdf.setFont(undefined, 'bold');
          pdf.text('ATTENDANCE REPORT', pageWidth / 2, 15, { align: 'center' });
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'normal');
          pdf.text(reportTitle, pageWidth / 2, 25, { align: 'center' });

          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'bold');
          pdf.text('Employee Name:', 14, 50);
          pdf.setFont(undefined, 'normal');
          pdf.text(employee.name || 'N/A', 55, 50);
          pdf.setFontSize(9);
          pdf.setTextColor(100, 100, 100);
          pdf.text(`Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth - 14, 50, { align: 'right' });

          autoTable(pdf, {
            startY: 60,
            head: [['Date', 'Day', 'Check-In', 'Check-Out', 'Total Hours', 'Status']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10, halign: 'center' },
            bodyStyles: { fontSize: 9, textColor: [0, 0, 0] },
            columnStyles: {
              0: { halign: 'center', cellWidth: 28 },
              1: { halign: 'left', cellWidth: 28 },
              2: { halign: 'center', cellWidth: 22 },
              3: { halign: 'center', cellWidth: 22 },
              4: { halign: 'center', cellWidth: 26 },
              5: { halign: 'center', cellWidth: 34 }
            },
            alternateRowStyles: { fillColor: [245, 245, 250] },
            margin: { top: 60, left: 14, right: 14 }
          });

          const finalY = pdf.lastAutoTable.finalY + 10;
          const summaryHeight = 70;
          if (finalY + summaryHeight > pageHeight - 20) {
            pdf.addPage();
            pageNum++;
          }
          const summaryY = finalY + summaryHeight > pageHeight - 20 ? 20 : finalY;
          if (summaryY === 20) pdf.setFillColor(249, 250, 251);
          else pdf.setFillColor(249, 250, 251);
          pdf.rect(14, summaryY, pageWidth - 28, 60, 'F');
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text('ATTENDANCE SUMMARY', 20, summaryY + 8);
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'normal');
          const colWidth = (pageWidth - 28) / 4;
          const sy = summaryY + 16;
          pdf.setFont(undefined, 'bold');
          pdf.text('Full Days:', 20, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(34, 197, 94);
          pdf.text(String(stats.fullDay), 20, sy + 6);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'bold');
          pdf.text('Half Days:', 20 + colWidth, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(234, 179, 8);
          pdf.text(String(stats.halfDay), 20 + colWidth, sy + 6);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'bold');
          pdf.text('Absent:', 20 + colWidth * 2, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(239, 68, 68);
          pdf.text(String(stats.absent), 20 + colWidth * 2, sy + 6);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, 'bold');
          pdf.text('Total Hours:', 20 + colWidth * 3, sy);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(139, 92, 246);
          pdf.text(formatTotalHours(stats.totalHours), 20 + colWidth * 3, sy + 6);
        }

        const fileName = `All_Employees_Attendance_${monthNames[parseInt(month) - 1]}_${year}.pdf`;
        pdf.save(fileName);
        toast.success('PDF downloaded successfully!');
        setLoading(false);
        return;
      }

      // Get employee details
      let employee;
      if (isAdmin) {
        employee = await fetchEmployeeDetails(selectedEmployees[0]);
        if (!employee) {
          toast.error('Employee data not found');
          setLoading(false);
          return;
        }
      } else {
        // For employee portal, use the logged-in user data
        if (!employeeData) {
          toast.error('User data not available. Please refresh the page.');
          setLoading(false);
          return;
        }
        employee = employeeData;
      }

      console.log('Employee data retrieved:', employee);

      // Fetch attendance data
      const employeeId = isAdmin ? selectedEmployees[0] : (employee._id || employee.id);
      console.log('Fetching attendance for employeeId:', employeeId);
      const attendanceData = await fetchAttendanceData(employeeId, month, year);
      console.log('Attendance data retrieved:', attendanceData.length, 'records');

      // Fetch holidays
      const holidaysEndpoint = isAdmin ? '/admin/holidays' : '/employee/holidays';
      const holidaysResponse = await api.get(holidaysEndpoint);
      const allHolidays = holidaysResponse.data;
      
      // Fetch approved leaves
      console.log('Fetching approved leaves...');
      const leavesEndpoint = isAdmin 
        ? `/admin/employee/${employeeId}/approved-leaves?month=${month}&year=${year}`
        : `/employee/approved-leaves?month=${month}&year=${year}`;
      const leavesResponse = await api.get(leavesEndpoint);
      const approvedLeaves = leavesResponse.data.leaves || [];
      console.log('Approved leaves retrieved:', approvedLeaves.length, 'leaves');

      const wfhEndpoint = isAdmin
        ? `/admin/employee/${employeeId}/approved-wfh?month=${month}&year=${year}`
        : `/employee/approved-wfh?month=${month}&year=${year}`;
      const wfhResponse = await api.get(wfhEndpoint);
      const approvedWfh = wfhResponse.data.wfhRequests || [];
      
      // Create leave map
      const leaveMap = {};
      approvedLeaves.forEach(leave => {
        leave.leaveDays?.forEach(day => {
          const dayDate = new Date(day.date);
          const dateKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
          leaveMap[dateKey] = day;
        });
      });

      const wfhMap = {};
      approvedWfh.forEach(wfh => {
        wfh.wfhDays?.forEach(day => {
          const dayDate = new Date(day.date);
          const dateKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
          wfhMap[dateKey] = day;
        });
      });
      
      // Create holiday map
      const holidayMap = {};
      allHolidays.forEach(holiday => {
        const holidayDate = new Date(holiday.date);
        const dateKey = `${holidayDate.getFullYear()}-${String(holidayDate.getMonth() + 1).padStart(2, '0')}-${String(holidayDate.getDate()).padStart(2, '0')}`;
        holidayMap[dateKey] = holiday.description;
      });

      // Create attendance map
      const attendanceMap = {};
      attendanceData.forEach(att => {
        const attDate = new Date(att.date);
        const dateKey = `${attDate.getFullYear()}-${String(attDate.getMonth() + 1).padStart(2, '0')}-${String(attDate.getDate()).padStart(2, '0')}`;
        attendanceMap[dateKey] = att;
      });

      // Get all days based on mode
      let allDays;
      let reportTitle;
      if (dateRangeMode === 'monthly') {
        allDays = getDaysInMonth(parseInt(year), parseInt(month));
        allDays = allDays.map(day => ({
          ...day,
          displayDate: `${String(day.date).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`
        }));
        reportTitle = `${monthNames[parseInt(month) - 1]} ${year}`;
      } else {
        allDays = getDaysInRange(startDate, endDate);
        const start = new Date(startDate);
        const end = new Date(endDate);
        reportTitle = `${String(start.getDate()).padStart(2, '0')}/${String(start.getMonth() + 1).padStart(2, '0')}/${start.getFullYear()} - ${String(end.getDate()).padStart(2, '0')}/${String(end.getMonth() + 1).padStart(2, '0')}/${end.getFullYear()}`;
      }

      // Prepare table data
      const tableData = allDays.map(day => {
        const attendance = attendanceMap[day.fullDate];
        const isFuture = isFutureDate(day.fullDate);
        const isHoliday = holidayMap[day.fullDate];
        const isSunday = day.dayName === 'Sunday';
        const isAltSaturday = isAltSaturdayHoliday(day.date, day.dayName, employee?.branch);
        const leaveDay = leaveMap[day.fullDate];
        const wfhDay = wfhMap[day.fullDate];

        if (isFuture) {
          return [
            day.displayDate,
            day.dayName,
            '-',
            '-',
            '-',
            'Future'
          ];
        }

        if (isHoliday) {
          return [
            day.displayDate,
            `${day.dayName} (${isHoliday})`,
            '-',
            '-',
            '-',
            'Full Day'
          ];
        }

        if (isSunday || isAltSaturday) {
          return [
            day.displayDate,
            day.dayName,
            '-',
            '-',
            '-',
            'Full Day'
          ];
        }

        // Check if there's a leave for this day
        if (leaveDay) {
          const leaveTypeText = leaveDay.leaveType === 'earned' ? 'Earned' : 
                                leaveDay.leaveType === 'sick' ? 'Sick' : 
                                leaveDay.leaveType === 'comp_off' ? 'Comp Off' :
                                leaveDay.leaveType === 'casual' ? 'Casual' : 'W/O Pay';
          const dayTypeText = leaveDay.dayType === 'half' ? 'Half Day' : 'Full Day';
          return [
            day.displayDate,
            day.dayName,
            '-',
            '-',
            '-',
            `${dayTypeText} ${leaveTypeText} Leave`
          ];
        }

        if (wfhDay) {
          const dayTypeText = wfhDay.dayType === 'half' ? 'Half Day' : 'Full Day';
          return [
            day.displayDate,
            day.dayName,
            '-',
            '-',
            '-',
            `${dayTypeText} WFH (Present)`
          ];
        }

        if (!attendance) {
          return [
            day.displayDate,
            day.dayName,
            '-',
            '-',
            '-',
            'Absent'
          ];
        }

        return [
          day.displayDate,
          day.dayName + (isHoliday ? ` (${isHoliday})` : ''),
          formatTime(attendance.checkInTime),
          formatTime(attendance.checkOutTime),
          formatTotalHours(attendance.totalHours),
          getStatusText(attendance.status)
        ];
      });

      // fullDay/halfDay/absent are tallied straight off tableData's Status column (not
      // recomputed separately from attendanceData), so the summary always reconciles
      // with the day-by-day rows built above for this employee's calendar.
    const stats = {
        fullDay: tableData.filter(row => row[5] === 'Full Day').length,
        halfDay: tableData.filter(row => row[5] === 'Half Day').length,
        absent: tableData.filter(row => row[5] === 'Absent').length,
        totalHours: attendanceData.reduce((sum, att) => sum + (att.totalHours || 0), 0),
        earnedLeaves: approvedLeaves.reduce((sum, leave) => 
          sum + leave.leaveDays.filter(day => day.leaveType === 'earned')
            .reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0
        ),
        sickLeaves: approvedLeaves.reduce((sum, leave) => 
          sum + leave.leaveDays.filter(day => day.leaveType === 'sick')
            .reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0
        ),
        casualLeaves: approvedLeaves.reduce((sum, leave) => 
          sum + leave.leaveDays.filter(day => day.leaveType === 'casual')
            .reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0
        ),
        withoutPayLeaves: approvedLeaves.reduce((sum, leave) => 
          sum + leave.leaveDays.filter(day => day.leaveType === 'without_pay')
            .reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0
        ),
        compOffLeaves: approvedLeaves.reduce((sum, leave) =>
          sum + leave.leaveDays.filter(day => day.leaveType === 'comp_off')
            .reduce((s, d) => s + (d.dayType === 'half' ? 0.5 : 1), 0), 0
        )
      };

      // Create PDF
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;

      // Add header
      pdf.setFillColor(139, 92, 246); // Purple color
      pdf.rect(0, 0, pageWidth, 40, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.text('ATTENDANCE REPORT', pageWidth / 2, 15, { align: 'center' });

      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(reportTitle, pageWidth / 2, 25, { align: 'center' });

      // Employee details
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'bold');
      pdf.text('Employee Name:', 14, 50);
      pdf.setFont(undefined, 'normal');
      pdf.text(employee.name || 'N/A', 55, 50);

      // Add generation date
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on: ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`, pageWidth - 14, 50, { align: 'right' });

      // Add attendance table
      autoTable(pdf, {
        startY: 60,
        head: [['Date', 'Day', 'Check-In', 'Check-Out', 'Total Hours', 'Status']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [139, 92, 246],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [0, 0, 0]
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 28 },
          1: { halign: 'left', cellWidth: 28 },
          2: { halign: 'center', cellWidth: 22 },
          3: { halign: 'center', cellWidth: 22 },
          4: { halign: 'center', cellWidth: 26 },
          5: { halign: 'center', cellWidth: 34 }
        },
        alternateRowStyles: {
          fillColor: [245, 245, 250]
        },
        margin: { top: 60, left: 14, right: 14 },
        didParseCell: function(data) {
          // Check if this row is a holiday or Sunday
          if (data.section === 'body') {
            const dayText = tableData[data.row.index][1];
            if (dayText.includes('(') && !dayText.includes('Sunday') && dayText.includes(')')) {
              // Sky blue background for holidays
              data.cell.styles.fillColor = [224, 242, 254]; // Sky blue #E0F2FE
            }
          }
          
          // Color code status column
          if (data.column.index === 5 && data.section === 'body') {
            const status = data.cell.text[0];
            if (status === 'Full Day') {
              data.cell.styles.textColor = [34, 197, 94]; // Green
              data.cell.styles.fontStyle = 'bold';
            } else if (status === 'Half Day') {
              data.cell.styles.textColor = [234, 179, 8]; // Yellow
              data.cell.styles.fontStyle = 'bold';
            } else if (status === 'Absent') {
              data.cell.styles.textColor = [239, 68, 68]; // Red
              data.cell.styles.fontStyle = 'bold';
            } else if (status === 'Future') {
              data.cell.styles.textColor = [156, 163, 175]; // Gray
              data.cell.styles.fontStyle = 'italic';
            } else if (status === 'Holiday') {
              data.cell.styles.textColor = [14, 165, 233]; // Sky blue
              data.cell.styles.fontStyle = 'bold';
            } else if (status.includes('Earned Leave')) {
              data.cell.styles.textColor = [16, 185, 129]; // Emerald
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [209, 250, 229]; // Light green
            } else if (status.includes('Sick Leave')) {
              data.cell.styles.textColor = [236, 72, 153]; // Pink
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [252, 231, 243]; // Light pink
            } else if (status.includes('Casual Leave')) {
              data.cell.styles.textColor = [168, 85, 247]; // Purple
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [243, 232, 255]; // Light purple
            } else if (status.includes('Comp Off Leave')) {
              data.cell.styles.textColor = [180, 83, 9]; // Amber
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [254, 243, 199]; // Light amber
            } else if (status.includes('W/O Pay Leave')) {
              data.cell.styles.textColor = [249, 115, 22]; // Orange
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [254, 235, 213]; // Light orange
            }
          }
        }
      });

      // Add summary section
      const finalY = pdf.lastAutoTable.finalY + 10;
      
      // Check if summary will fit on current page, if not add new page
      if (finalY + 70 > pageHeight - 20) {
        pdf.addPage();
        const newPageY = 20;
        
        pdf.setFillColor(249, 250, 251);
        pdf.rect(14, newPageY, pageWidth - 28, 60, 'F');

        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('ATTENDANCE SUMMARY', 20, newPageY + 8);

        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        
        const summaryY = newPageY + 16;
        const colWidth = (pageWidth - 28) / 4;

        // Full Days
        pdf.setFont(undefined, 'bold');
        pdf.text('Full Days:', 20, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(34, 197, 94);
        pdf.text(String(stats.fullDay), 20, summaryY + 6);

        // Half Days
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Half Days:', 20 + colWidth, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(234, 179, 8);
        pdf.text(String(stats.halfDay), 20 + colWidth, summaryY + 6);

        // Absent Days
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Absent:', 20 + colWidth * 2, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(239, 68, 68);
        pdf.text(String(stats.absent), 20 + colWidth * 2, summaryY + 6);

        // Total Hours
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Total Hours:', 20 + colWidth * 3, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(139, 92, 246);
        pdf.text(formatTotalHours(stats.totalHours), 20 + colWidth * 3, summaryY + 6);

        // Leave Summary (Second Row)
        const leaveSummaryY = summaryY + 16;
        
        // Earned Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Earned Leaves:', 20, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(16, 185, 129);
        pdf.text(String(stats.earnedLeaves), 20, leaveSummaryY + 6);

        // Sick Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Sick Leaves:', 20 + colWidth, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(236, 72, 153);
        pdf.text(String(stats.sickLeaves), 20 + colWidth, leaveSummaryY + 6);

        // Casual Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Casual Leaves:', 20 + colWidth * 2, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(168, 85, 247);
        pdf.text(String(stats.casualLeaves), 20 + colWidth * 2, leaveSummaryY + 6);

        // W/O Pay Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('W/O Pay Leaves:', 20 + colWidth * 3, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(249, 115, 22);
        pdf.text(String(stats.withoutPayLeaves), 20 + colWidth * 3, leaveSummaryY + 6);

        // Footer position for new page
        const footerY = pageHeight - 10;
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('This is a system-generated report', pageWidth / 2, footerY, { align: 'center' });
        pdf.text(`Page 1 of 1`, pageWidth - 14, footerY, { align: 'right' });
      } else {
        pdf.setFillColor(249, 250, 251);
        pdf.rect(14, finalY, pageWidth - 28, 60, 'F');

        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('ATTENDANCE SUMMARY', 20, finalY + 8);

        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        
        const summaryY = finalY + 16;
        const colWidth = (pageWidth - 28) / 4;

        // Full Days
        pdf.setFont(undefined, 'bold');
        pdf.text('Full Days:', 20, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(34, 197, 94);
        pdf.text(String(stats.fullDay), 20, summaryY + 6);

        // Half Days
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Half Days:', 20 + colWidth, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(234, 179, 8);
        pdf.text(String(stats.halfDay), 20 + colWidth, summaryY + 6);

        // Absent Days
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Absent:', 20 + colWidth * 2, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(239, 68, 68);
        pdf.text(String(stats.absent), 20 + colWidth * 2, summaryY + 6);

        // Total Hours
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Total Hours:', 20 + colWidth * 3, summaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(139, 92, 246);
        pdf.text(formatTotalHours(stats.totalHours), 20 + colWidth * 3, summaryY + 6);

        // Leave Summary (Second Row)
        const leaveSummaryY = summaryY + 16;
        
        // Earned Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Earned Leaves:', 20, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(16, 185, 129);
        pdf.text(String(stats.earnedLeaves), 20, leaveSummaryY + 6);

        // Sick Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Sick Leaves:', 20 + colWidth, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(236, 72, 153);
        pdf.text(String(stats.sickLeaves), 20 + colWidth, leaveSummaryY + 6);

        // Casual Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('Casual Leaves:', 20 + colWidth * 2, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(168, 85, 247);
        pdf.text(String(stats.casualLeaves), 20 + colWidth * 2, leaveSummaryY + 6);

        // W/O Pay Leaves
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'bold');
        pdf.text('W/O Pay Leaves:', 20 + colWidth * 3, leaveSummaryY);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(249, 115, 22);
        pdf.text(String(stats.withoutPayLeaves), 20 + colWidth * 3, leaveSummaryY + 6);

        // Footer position for same page
        const footerY = pageHeight - 10;
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('This is a system-generated report', pageWidth / 2, footerY, { align: 'center' });
        pdf.text(`Page 1 of 1`, pageWidth - 14, footerY, { align: 'right' });
      }

      // Save the PDF
      const fileNameDate = dateRangeMode === 'monthly' 
        ? `${monthNames[parseInt(month) - 1]}_${year}`
        : reportTitle.replace(/\//g, '-');
      const fileName = `${employee.name.replace(/\s+/g, '_')}_Attendance_${fileNameDate}.pdf`;
      pdf.save(fileName);

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate PDF';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const generateExcel = async () => {
    const isAllEmployees = isAdmin && reportScope === 'all';
    const isMultiSelect = isAdmin && reportScope === 'single' && selectedEmployees.length > 1;
    if (isAdmin && reportScope === 'single' && selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    if (isAllEmployees && dateRangeMode !== 'monthly') {
      toast.error('All Employee report is available for monthly report only. Please select Month and Year.');
      return;
    }

    if (isMultiSelect && dateRangeMode !== 'monthly') {
      toast.error('Multi-employee report is available for monthly report only. Please select Month and Year.');
      return;
    }

    // Validate based on mode
    if (dateRangeMode === 'monthly') {
      if (!selectedMonth || !selectedYear) {
        toast.error('Please select month and year');
        return;
      }
    } else {
      if (!startDate || !endDate) {
        toast.error('Please select start and end dates');
        return;
      }
      if (new Date(startDate) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    try {
      setLoadingExcel(true);

      // Determine month/year from mode
      let month, year;
      if (dateRangeMode === 'monthly') {
        month = selectedMonth;
        year = selectedYear;
      } else {
        // Use the start date's month/year for range mode
        const start = new Date(startDate);
        month = String(start.getMonth() + 1);
        year = String(start.getFullYear());
      }

      // --- All Employees Excel: one workbook, one sheet per employee ---
      if (isAllEmployees) {
        const holidaysResponse = await api.get('/admin/holidays');
        const allHolidays = holidaysResponse.data || [];
        const holidayMap = {};
        allHolidays.forEach(holiday => {
          const d = new Date(holiday.date);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          holidayMap[key] = holiday.description;
        });

        const workbook = XLSX.utils.book_new();
        const usedSheetNames = new Set();

        const sanitizeSheetName = (name) => {
          let s = (name || 'Employee').replace(/[:/\\?*[\]]/g, ' ').trim().slice(0, 31);
          if (!s) s = 'Sheet';
          let base = s;
          let n = 1;
          while (usedSheetNames.has(s)) {
            s = (base.slice(0, 31 - String(n).length - 1) + '_' + n).slice(0, 31);
            n++;
          }
          usedSheetNames.add(s);
          return s;
        };

        for (const employee of employees) {
          const { tableData, stats } = await buildEmployeeReportData(employee, month, year, holidayMap);
          const headers = [['Date', 'Day', 'Check-In', 'Check-Out', 'Total Hours', 'Status']];
          const summaryRows = [
            [],
            ['ATTENDANCE SUMMARY', '', '', '', '', ''],
            ['Full Days', stats.fullDay, 'Half Days', stats.halfDay, 'Absent', stats.absent],
            ['Total Hours', formatTotalHours(stats.totalHours), 'Earned Leaves', stats.earnedLeaves, 'Sick Leaves', stats.sickLeaves],
            ['Casual Leaves', stats.casualLeaves, 'Comp Off Leaves', stats.compOffLeaves, 'W/O Pay Leaves', stats.withoutPayLeaves]
          ];
          const wsData = [...headers, ...tableData, ...summaryRows];
          const worksheet = XLSX.utils.aoa_to_sheet(wsData);
          const sheetName = sanitizeSheetName(employee.name);
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        }

        const fileName = `All_Employees_Attendance_${monthNames[parseInt(month) - 1]}_${year}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        toast.success('Excel file downloaded successfully!');
        setLoadingExcel(false);
        return;
      }

      // --- Multi-select employees Excel: one sheet per selected employee ---
      if (isMultiSelect) {
        const holidaysResponse = await api.get('/admin/holidays');
        const allHolidays = holidaysResponse.data || [];
        const holidayMap = {};
        allHolidays.forEach(holiday => {
          const d = new Date(holiday.date);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          holidayMap[key] = holiday.description;
        });

        const workbook = XLSX.utils.book_new();
        const usedSheetNames = new Set();

        const sanitizeSheetName = (name) => {
          let s = (name || 'Employee').replace(/[:/\\?*[\]]/g, ' ').trim().slice(0, 31);
          if (!s) s = 'Sheet';
          let base = s;
          let n = 1;
          while (usedSheetNames.has(s)) {
            s = (base.slice(0, 31 - String(n).length - 1) + '_' + n).slice(0, 31);
            n++;
          }
          usedSheetNames.add(s);
          return s;
        };

        const employeesToReport = employees.filter(emp => selectedEmployees.includes(emp._id));
        for (const employee of employeesToReport) {
          const { tableData, stats } = await buildEmployeeReportData(employee, month, year, holidayMap);
          const headers = [['Date', 'Day', 'Check-In', 'Check-Out', 'Total Hours', 'Status']];
          const summaryRows = [
            [],
            ['ATTENDANCE SUMMARY', '', '', '', '', ''],
            ['Full Days', stats.fullDay, 'Half Days', stats.halfDay, 'Absent', stats.absent],
            ['Total Hours', formatTotalHours(stats.totalHours), 'Earned Leaves', stats.earnedLeaves, 'Sick Leaves', stats.sickLeaves],
            ['Casual Leaves', stats.casualLeaves, 'Comp Off Leaves', stats.compOffLeaves, 'W/O Pay Leaves', stats.withoutPayLeaves]
          ];
          const wsData = [...headers, ...tableData, ...summaryRows];
          const worksheet = XLSX.utils.aoa_to_sheet(wsData);
          const sheetName = sanitizeSheetName(employee.name);
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        }

        const fileName = `Attendance_${employeesToReport.length}_Employees_${monthNames[parseInt(month) - 1]}_${year}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        toast.success('Excel file downloaded successfully!');
        setLoadingExcel(false);
        return;
      }

      // Determine the API endpoint (single employee)
      let url;
      if (dateRangeMode === 'monthly') {
        if (isAdmin) {
          url = `/admin/attendance/employee/${selectedEmployees[0]}/download-excel?month=${month}&year=${year}`;
        } else {
          url = `/employee/attendance/download-excel?month=${month}&year=${year}`;
        }
      } else {
        // For range mode, pass startDate and endDate
        if (isAdmin) {
          url = `/admin/attendance/employee/${selectedEmployees[0]}/download-excel?startDate=${startDate}&endDate=${endDate}`;
        } else {
          url = `/employee/attendance/download-excel?startDate=${startDate}&endDate=${endDate}`;
        }
      }

      // Make API request
      const response = await api.get(url, {
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Attendance_Report.xlsx';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error generating Excel:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate Excel file';
      toast.error(errorMessage);
    } finally {
      setLoadingExcel(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
      <div className="flex items-center space-x-2 mb-4">
        <Download className="w-5 h-5 text-purple-600" />
        <h3 className="text-lg font-semibold text-gray-800">Download Attendance Report</h3>
      </div>

      <div className="space-y-4">
        {/* Employee Selection (Admin Only) */}
        {isAdmin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Select Employee
            </label>
            <div className="flex items-center gap-4 mb-2">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="reportScope"
                  value="all"
                  checked={reportScope === 'all'}
                  onChange={() => {
                    setReportScope('all');
                    setSelectedEmployees([]);
                    setDateRangeMode('monthly');
                  }}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                <span className="ml-2 text-sm text-gray-700">All Employee</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="reportScope"
                  value="single"
                  checked={reportScope === 'single'}
                  onChange={() => setReportScope('single')}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                />
                <span className="ml-2 text-sm text-gray-700">Select Employee</span>
              </label>
            </div>
            {reportScope === 'single' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-500">
                    {selectedEmployees.length} employee(s) selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedEmployees(employees.map(e => e._id))}
                      className="text-xs font-medium text-purple-600 hover:text-purple-700"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSelectedEmployees([])}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto p-2 bg-gray-50/50">
                  {loadingEmployees ? (
                    <p className="text-sm text-gray-500 py-2">Loading employees...</p>
                  ) : (
                    employees.map(emp => (
                      <label
                        key={emp._id}
                        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-purple-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(emp._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployees(prev => {
                                const next = [...prev, emp._id];
                                if (next.length > 1) setDateRangeMode('monthly');
                                return next;
                              });
                            } else {
                              setSelectedEmployees(prev => prev.filter(id => id !== emp._id));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">
                          {emp.name} {emp.employeeId || emp.email ? `(${emp.employeeId || emp.email})` : ''}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Date Range Mode Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Report Type
          </label>
          <div className="flex space-x-2">
            <button
              onClick={() => setDateRangeMode('monthly')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                dateRangeMode === 'monthly'
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setDateRangeMode('range')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                dateRangeMode === 'range'
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } ${(isAdmin && reportScope === 'all') || (isAdmin && reportScope === 'single' && selectedEmployees.length > 1) ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={(isAdmin && reportScope === 'all') || (isAdmin && reportScope === 'single' && selectedEmployees.length > 1)}
              title={isAdmin && (reportScope === 'all' || selectedEmployees.length > 1) ? 'Multi-employee report is monthly only' : ''}
            >
              Date Range
            </button>
          </div>
        </div>

        {/* Conditional Date Selection */}
        {dateRangeMode === 'monthly' ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">-- Select Month --</option>
                {monthNames.map((month, index) => (
                  <option key={index} value={String(index + 1)}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">-- Select Year --</option>
                {years.map(year => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Download Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={generatePDF}
            disabled={
              loading ||
              (isAdmin && reportScope === 'single' && selectedEmployees.length === 0) ||
              (dateRangeMode === 'monthly' && (!selectedMonth || !selectedYear)) ||
              (dateRangeMode === 'range' && (!startDate || !endDate))
            }
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span className="text-sm">PDF...</span>
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                <span className="text-sm">Download PDF</span>
              </>
            )}
          </button>

          <button
            onClick={generateExcel}
            disabled={
              loadingExcel ||
              (isAdmin && reportScope === 'single' && selectedEmployees.length === 0) ||
              (dateRangeMode === 'monthly' && (!selectedMonth || !selectedYear)) ||
              (dateRangeMode === 'range' && (!startDate || !endDate))
            }
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {loadingExcel ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span className="text-sm">Excel...</span>
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-5 h-5" />
                <span className="text-sm">Download Excel</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceDownloadPDF;
